/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import {
  ADD_FAMILY_INFORMATION_FACADE_TOKEN,
  IAddFamilyInformationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { PropertiesListItemsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-properties-list-vm';
import { SysqueSwitchButtonVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-switch-button-vm';
import { SysqueDropdownConnectorsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-dropdown-connectors-vm';
import {
  AddFamilyInformationData,
  AddFamilyInformationReturnCodes,
  FamilyInformationItem,
} from '../../../models/family-information-model';
import { GlobalConstants } from '../../../constants/global-constants';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { IFamilyInformationItem } from '../add-family-information-types';
import { AddFamilyProjectDataInfo } from '../../../../../../utils/src/lib/common-helper/add-family-project-data-info';
import { Subscription } from 'rxjs';
import { Guid } from 'guid-typescript';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-user-custom-content-family-information',
  templateUrl: './user-custom-content-family-information.component.html',
  styleUrl: './user-custom-content-family-information.component.scss',
  standalone: false
})
export class UserCustomContentFamilyInformation implements OnInit {
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public title = '';
  public image = '';
  public propertiesListItems: PropertiesListItemsVM[] = [];
  public isadditionalWireLengthVisible: boolean = true;
  public additionalWireLengthSpinButtons: boolean = true;
  public additionalWireLengthItems: SysqueNumberBoxVM[] = [];
  public connectorsTitle: string;
  public connectorsItems: SysqueDropdownConnectorsVM[] = [];
  public isSupportDisciplineVisible: boolean = false;
  public supportDisciplineLabel: string;
  public supportDiscipline: string;
  public instanceDescriptionLabel = '';
  public instanceDescription = '';
  public typeDescriptionLabel = '';
  public typeDescription = '';
  public projectDataButtonInfo: SysqueSwitchButtonVM;
  public item: IFamilyInformationItem;
  public isProjectDataBtnDisabled = false;
  public fileName = '';
  public selectedPathSubscription: Subscription;
  public openPdfSubscription: Subscription;
  public getUserCustomImageSubscription: Subscription;

  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(
    @Inject(ADD_FAMILY_INFORMATION_FACADE_TOKEN) private addFamilyInformationFacade: IAddFamilyInformationFacade
  ) {}

  public ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.addFamilyInformationFacade.registerControllers();
    const response = this.addFamilyInformationFacade.getFamilyInformationResponse();
    if (response.Data.IsFromAdd) {
      this.item = this.addFamilyInformationFacade.setItemToAdd(response.Data);
    } else {
      this.item = this.addFamilyInformationFacade.getFamilyInformationItem();
    }
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.PROJECT_PARAMS, []);
    this.setFamilyInformationData(response.Data, this.item);
    this.handleMessages(response);
    this.initData();
  }

  public ngOnDestroy(): void {
    if (this.selectedPathSubscription) {
      this.selectedPathSubscription.unsubscribe();
    }
    if (this.openPdfSubscription) {
      this.openPdfSubscription.unsubscribe();
    }
    if (this.getUserCustomImageSubscription) {
      this.getUserCustomImageSubscription.unsubscribe();
    }
  }

  //#region data initialization
  private initData(): void {
    this.titleInit();
    this.initGuid();
    this.imageInit();
    this.propertiesInit();
    this.addtionalWireLengthInit();
    this.connectorsInit();
    this.instanceDescriptionInit();
    this.typeDescriptionInit();
    this.pdfLocationInit();
    this.projectDataInit();
  }

  private titleInit(): void {
    this.title = 'Custom Content';
  }

  private initGuid(): void {
    this.item.guid = this.item.isFromEdit ? this.item.guid : Guid.create().toString();
  }

  private imageInit(): void {
    this.image = 'data:image/png;base64,' + this.item.image;
  }

  private propertiesInit(): void {
    this.propertiesListItems = [
      {
        label: GlobalConstants.DISCIPLINE_LABEL,
        value: this.item.supportDiscipline,
        isEditable: false,
      },
      {
        label: GlobalConstants.PART_TYPE_LABEL,
        value: this.item.fittingType,
        isEditable: false,
      },
      {
        label: GlobalConstants.CATEGORY_LABEL,
        value: this.item.familyCategoryName,
        isEditable: false,
      },
      {
        label: GlobalConstants.MANUFACTURER_LABEL,
        value: this.item.manufacturer,
        isEditable: true,
      },
      {
        label: GlobalConstants.PART_ID_LABEL,
        value: this.item.model,
        isEditable: true,
      },
      {
        label: GlobalConstants.NOMINAL_SIZE_LABEL,
        value: this.item.nominalSize,
        isEditable: true,
      },
    ];
  }

  private addtionalWireLengthInit(): void {
    this.additionalWireLengthItems = [
      {
        label: GlobalConstants.ADDED_WIRE_LENGTH_LABEL,
        value: Number(this.item.addedWireLength),
        isMetric: this.isMetric,
        height: '30',
        width: '122',
        minValue: 0
      },
    ];
  }

  private connectorsInit(): void {
    if(this.item.connectorCount === 0) {
      return;
    }
    const itemConnectors = this.item.connectors;
    let selectedConnectors = [];
    if (itemConnectors) {
      selectedConnectors = itemConnectors?.split(' x ').map(x => x.trim());
    }
    this.connectorsTitle = '';
    const connectors = this.addFamilyInformationFacade.getUserCustomContentConnectors();
    const connectorsCount = this.item.connectorCount;
    for (let i = 0; i < connectorsCount; i++) {
      this.connectorsItems.push({
        label: '',
        data: connectors.map((x) => x.connector),
        selectedValue: Array.isArray(selectedConnectors)
        ? connectors?.find(x => x.connector === selectedConnectors[i])?.connector
              : connectors?.find(x => x.connector === selectedConnectors)?.connector,    
        width: '152'
      });
    }
  }

  private instanceDescriptionInit(): void {
    this.instanceDescriptionLabel = GlobalConstants.INSTANCE_DESCRIPTION_LABEL;
    this.instanceDescription = this.item.descriptionInstance;
  }

  private typeDescriptionInit(): void {
    this.typeDescriptionLabel = GlobalConstants.TYPE_DESCRIPTION_LABEL;
    this.typeDescription = this.item.descriptionType;
  }

  private pdfLocationInit(): void {
    this.fileName = !this.item.pdfLocation ? '' : this.item.pdfLocation.split('\\').pop()?.split('/').pop() || '';
  }

  private projectDataInit(): void {
    this.projectDataButtonInfo = {
      title: GlobalConstants.PROJECT_DATA_LABEL,
      enabled: Array.isArray(this.item.parameters) && this.item.parameters.length > 0,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: true,
    };
  }

  //#endregion

  private handleMessages(response: FamilyInformationItem): void {
    if (
      response.ReturnCode == AddFamilyInformationReturnCodes.Success ||
      response.ReturnCode == AddFamilyInformationReturnCodes.NoMessage
    ) {
      this.hideAlert();
    }
  }

  public onButtonClick(): void {
    this.getUserCustomImageSubscription = this.addFamilyInformationFacade
      .getUserCustomImage(this.item.systemName, this.item.name)
      .subscribe((response) => {
        if (response !== '' && response !== null && response !== undefined) {
          this.image = 'data:image/png;base64,' + response;
          this.item.image = response;
        }
      });
  }

  public onPropertiesValueChanged($event: { value: string; label: string }): void {
    switch ($event.label) {
      case GlobalConstants.DISCIPLINE_LABEL:
        this.item.supportDiscipline = $event.value;
        break;
      case GlobalConstants.PART_TYPE_LABEL:
        this.item.fittingType = $event.value.toString();
        break;
      case GlobalConstants.CATEGORY_LABEL:
        this.item.category = $event.value.toString();
        break;
      case GlobalConstants.MANUFACTURER_LABEL:
        this.item.manufacturer = $event.value;
        break;
      case GlobalConstants.PART_ID_LABEL:
        this.item.model = $event.value.toString();
        break;
      case GlobalConstants.NOMINAL_SIZE_LABEL:
        this.item.nominalSize = $event.value.toString();
        break;
    }
  }

  public onAdditionalWireLengthValueChanged($event: { value: string; label: string }): void {
    this.item.addedWireLength = $event.value;
  }

  public onInstanceDescriptionValueChanged($event: string): void {
    this.item.descriptionInstance = $event;
  }

  public onTypeDescriptionValueChanged($event: string): void {
    this.item.descriptionType = $event;
  }

  public onAttachFileClicked(): void {
    this.selectedPathSubscription = this.addFamilyInformationFacade.getPdfLocationPath().subscribe((data) => {
      if (!data) {
        return;
      }
      this.fileName = data.split('\\').pop()?.split('/').pop() || '';
      this.item.pdfLocation = data;
    });
  }

  public onOpenPdf(): void {
    this.openPdfSubscription = this.addFamilyInformationFacade
      .openPdfFile(this.item.pdfLocation)
      .subscribe((response) => {
        if (response == AddFamilyInformationReturnCodes.PdfDoesNotExist) {
          this.showAlert(GlobalConstants.PDF_FILE_DOES_NOT_EXIST, AlertStatus.Warning);
        }
      });
  }

  public onConnectorValueChanged($event: { value: string; label: string; index: number }): void {
    let selectedConnectors;
    if(this.item.connectors === null) {
      selectedConnectors = [];
    } else {
      selectedConnectors = this.item.connectors.split(' x ').map((x) => x.trim());
    }
    selectedConnectors[$event.index] = $event.value;
    this.item.connectors = selectedConnectors.join(' x ');
  }

  public doneClick(): void {
    if (!this.validateInput()) {
      return;
    }
    new AddFamilyProjectDataInfo(this.addFamilyInformationFacade).getModifiedProjectParameters(
      this.item,
      this.isProjectDataBtnDisabled
    );
    //dispatch saveFamilyInformationInXml - call the backend to save the information in the xml file
    this.addFamilyInformationFacade.saveFamilyInformationInXml(this.item);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'familyInformationSaveResponse') {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == AddFamilyInformationReturnCodes.Success) {
        this.addFamilyInformationFacade.updateItem(this.item);
        this.addFamilyInformationFacade.sendClosePopupAction();
      }
    }
  }

  public onProjectDataButtonClicked(): void {
    this.addFamilyInformationFacade.onClickedToOpenProjectData();
  }

  public onProjectDataSwitchClicked(button: SysqueSwitchButtonVM): void {
    this.isProjectDataBtnDisabled = !button.enabled;
  }

  public validateInput(): boolean {
    if (this.item.addedWireLength === null || this.item.addedWireLength === '') {
      this.showAlert(GlobalConstants.ENTER_ADDED_WIRE_LENGTH, AlertStatus.Warning);
      return false;
    }
    if (this.item.manufacturer === null || this.item.manufacturer === '') {
      this.showAlert(GlobalConstants.ENTER_MANUFACTURER_NAME, AlertStatus.Warning);
      return false;
    }
    if (this.item.model === null || this.item.model === '') {
      this.showAlert(GlobalConstants.ENTER_PART_ID, AlertStatus.Warning);
      return false;
    }
    if (this.item.nominalSize === null || this.item.nominalSize === '') {
      this.showAlert(GlobalConstants.ENTER_NOMINAL_SIZE, AlertStatus.Warning);
      return false;
    }
    if (this.item.pdfLocation === null || this.item.pdfLocation === '') {
      this.showAlert(GlobalConstants.SELECT_PDF_PATH, AlertStatus.Warning);
      return false;
    }
    return true;
  }

  //#region alert
  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  public hideAlert(): void {
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  //#endregion

  private get isMetric(): boolean {
    return this.item.unit === GlobalConstants.METRIC;
  }

  private setFamilyInformationData(data: AddFamilyInformationData, item: IFamilyInformationItem): void {
    item.connectorCount = data.ConnectorCount;
    item.familyCategoryName = data.FamilyCategoryName;
    item.jBoxType = data.JBoxType;
    item.partType = item.fittingType;
    item.manufacturer ||= data.Manufacturer;
    item.supportDiscipline ||= data.SupportDisciplines;
    item.model ||= data.Model;
    item.nominalSize ||= data.NominalSize;
    item.pdfLocation ||= data.PdfLocation;
    item.addedWireLength ||= data.Length;
    if (!item.isFromEdit) {
      item.guid = data.Guid;
    }
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);
  }
}
